import React, { useEffect, useState } from 'react';
import { Users, DollarSign, GraduationCap, AlertTriangle, TrendingUp, BarChart3 } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface MetricCardWidgetProps {
  config: {
    metricType?: string;
  };
  size: WidgetSize;
}

const MetricCardWidget: React.FC<MetricCardWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [value, setValue] = useState<string | number>('0');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [icon, setIcon] = useState<React.ReactNode>(null);
  const [color, setColor] = useState('text-primary-600');

  useEffect(() => {
    loadMetric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.metricType, userProfile?.empresaCnpj]);

  const loadMetric = async () => {
    if (!userProfile?.empresaCnpj || !config.metricType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const metrics = await firebaseService.getDashboardMetrics(userProfile.empresaCnpj);

      switch (config.metricType) {
        case 'alunos-ativos':
          setValue(metrics.alunosAtivos || 0);
          setDescription('Alunos matriculados');
          setIcon(<Users className="h-6 w-6" />);
          setColor('text-success-600');
          break;
        case 'mensalidades-pagas':
          setValue(`R$ ${(metrics.valorRecebido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          setDescription('Este mês');
          setIcon(<DollarSign className="h-6 w-6" />);
          setColor('text-primary-600');
          break;
        case 'turmas-ativas':
          setValue(metrics.turmasAtivas || 0);
          setDescription('Em funcionamento');
          setIcon(<GraduationCap className="h-6 w-6" />);
          setColor('text-secondary-600');
          break;
        case 'inadimplencia':
          setValue(`R$ ${(metrics.valorVencido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          setDescription(`${metrics.mensalidadesVencidas || 0} mensalidades`);
          setIcon(<AlertTriangle className="h-6 w-6" />);
          setColor('text-danger-600');
          break;
        case 'receita-total':
          setValue(`R$ ${(metrics.valorRecebido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          setDescription('Receita acumulada');
          setIcon(<TrendingUp className="h-6 w-6" />);
          setColor('text-primary-600');
          break;
        case 'taxa-ocupacao':
          const turmas = await firebaseService.getTurmas(userProfile.empresaCnpj);
          const turmasComLimite = turmas.filter(t => (t.limiteAlunos || 0) > 0);
          if (turmasComLimite.length > 0) {
            const ocupacaoMedia = turmasComLimite.reduce((sum, t) => {
              let alunosIds: string[] = [];
              if (typeof t.alunosIds === 'string') {
                try {
                  alunosIds = JSON.parse(t.alunosIds);
                } catch {
                  alunosIds = [];
                }
              } else if (Array.isArray(t.alunosIds)) {
                alunosIds = t.alunosIds;
              }
              const ocupacao = alunosIds.length / (t.limiteAlunos || 1);
              return sum + ocupacao;
            }, 0) / turmasComLimite.length;
            setValue(`${Math.round(ocupacaoMedia * 100)}%`);
            setDescription('Ocupação média');
            setIcon(<BarChart3 className="h-6 w-6" />);
            setColor('text-secondary-600');
          } else {
            setValue('0%');
            setDescription('Sem dados');
            setIcon(<BarChart3 className="h-6 w-6" />);
            setColor('text-gray-400');
          }
          break;
        default:
          setValue('0');
          setDescription('Métrica não configurada');
      }
    } catch (error) {
      console.error('Erro ao carregar métrica:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const textSize = size === 'small' ? 'text-xl' : size === 'medium' ? 'text-2xl' : 'text-3xl';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 mb-1">{description}</p>
          <p className={`font-bold ${textSize} ${color}`}>{value}</p>
        </div>
        <div className={`${color} opacity-60`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default MetricCardWidget;

